/**
 * Prizes module DTOs (§6, §15.2 `/admin/prizes`, `/admin/brands`): catalogue, products, option axes, SKU
 * variants, brand approval and stock reconciliation. Stock is enforced by unique prize-unit constraints.
 */
import { z } from 'zod';
import { CountryCode, HexColor, IsoDateTime, LocalizedText, Ulid } from './common.js';
import { FulfillmentType, PrizeTier, SelectionMode } from './enums.js';
import { FulfillmentOwnerSchema, SubstitutionPolicy } from './lottery-config.js';
import { MoneyDto, SupportedMoneyDto } from './money.js';
export const PrizeCategory = z.enum([
    'PHONE',
    'CAR',
    'LAPTOP',
    'APPLIANCE',
    'FASHION',
    'VOUCHER',
    'CASH',
    'OTHER',
]);
export const AssetDto = z.object({
    assetId: Ulid,
    ref: z.string(),
    url: z.string().optional(),
    alt: z.string().min(1),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
    format: z.enum(['AVIF', 'WEBP', 'PNG', 'JPEG', 'SVG', 'MP4']),
    rightsConfirmed: z.boolean(),
});
export const BrandDto = z.object({
    brandId: Ulid,
    name: z.string(),
    logo: AssetDto.optional(),
    approved: z.boolean(),
    approvedBy: Ulid.nullable(),
    approvedAt: IsoDateTime.nullable(),
    sponsorId: Ulid.nullable(),
});
export const UpsertBrandRequest = z.object({
    brandId: Ulid.optional(),
    name: z.string().min(1).max(120),
    logoRef: z.string().max(512).optional(),
    sponsorId: Ulid.optional(),
});
export const ApproveBrandRequest = z.object({ approved: z.boolean(), note: z.string().max(500).optional() });
export const OptionAxisInput = z.object({
    axisId: z.string().regex(/^[a-z][a-z0-9_]{0,31}$/),
    name: LocalizedText,
    values: z
        .array(z.object({
        valueId: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,31}$/),
        label: LocalizedText,
        swatch: HexColor.optional(),
    }))
        .min(1),
});
export const VariantInput = z.object({
    variantId: Ulid.optional(),
    sku: z.string().min(1).max(64),
    options: z.record(z.string(), z.string()),
    value: SupportedMoneyDto,
    specifications: z.record(z.string(), z.string()).default({}),
    imageRefs: z.array(z.string().max(512)).default([]),
    stock: z.number().int().min(0),
});
export const UpsertProductRequest = z.object({
    productId: Ulid.optional(),
    brandId: Ulid,
    name: LocalizedText,
    description: LocalizedText,
    category: PrizeCategory,
    heroImageRef: z.string().max(512),
    galleryRefs: z.array(z.string().max(512)).default([]),
    videoRef: z.string().max(512).optional(),
    specifications: z.record(z.string(), LocalizedText).default({}),
    inTheBox: z.array(LocalizedText).default([]),
    optionAxes: z.array(OptionAxisInput).default([]),
    variants: z.array(VariantInput).min(1),
});
/** Generates the variant matrix from option axes (§6.3 admin catalogue builder). */
export const GenerateVariantMatrixRequest = z.object({
    optionAxes: z.array(OptionAxisInput).min(1),
    baseValue: SupportedMoneyDto,
    skuPrefix: z.string().max(32),
});
export const UpsertPrizeRequest = z.object({
    prizeId: Ulid.optional(),
    /** Null / absent = tenant catalogue item reusable across lotteries (§6.1); set to attach to one lottery. */
    lotteryId: Ulid.nullable().optional(),
    name: LocalizedText,
    description: LocalizedText.optional(),
    category: PrizeCategory,
    tier: PrizeTier,
    quantity: z.number().int().min(1),
    value: SupportedMoneyDto,
    fulfillmentType: FulfillmentType,
    fulfillmentOwner: FulfillmentOwnerSchema,
    sponsorId: Ulid.optional(),
    selectionMode: SelectionMode,
    productId: Ulid.optional(),
    approvedBrandIds: z.array(Ulid).default([]),
    valueCap: SupportedMoneyDto.optional(),
    accentColor: HexColor.optional(),
    heroImageRef: z.string().max(512).optional(),
    galleryRefs: z.array(z.string().max(512)).default([]),
    eligibleRegions: z.array(CountryCode).default([]),
    substitutionPolicy: SubstitutionPolicy.default('EQUIVALENT_WITH_CONSENT'),
});
/** §6.2: every organizer report reconciles these buckets. */
export const PrizeStockDto = z.object({
    catalogStock: z.number().int(),
    mainReserved: z.number().int(),
    samplingReserved: z.number().int(),
    held: z.number().int(),
    won: z.number().int(),
    fulfilled: z.number().int(),
    available: z.number().int(),
});
export const PrizeAdminDto = z.object({
    prizeId: Ulid,
    lotteryId: Ulid.nullable(),
    name: LocalizedText,
    category: PrizeCategory,
    tier: PrizeTier,
    quantity: z.number().int(),
    value: MoneyDto,
    fulfillmentType: FulfillmentType,
    fulfillmentOwner: FulfillmentOwnerSchema,
    sponsorId: Ulid.nullable(),
    selectionMode: SelectionMode,
    productId: Ulid.nullable(),
    approvedBrandIds: z.array(Ulid),
    valueCap: MoneyDto.nullable(),
    accentColor: HexColor.nullable(),
    stock: PrizeStockDto,
    /** Active sponsor reservations cannot be edited downward after the first qualified entry (§6.2). */
    reservationLocked: z.boolean(),
    assetIssues: z.array(z.object({ ref: z.string(), issue: z.string() })),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const PrizeUnitDto = z.object({
    prizeUnitId: Ulid,
    prizeId: Ulid,
    variantId: Ulid.nullable(),
    status: z.enum(['AVAILABLE', 'RESERVED_MAIN', 'RESERVED_SAMPLING', 'HELD', 'WON', 'FULFILLED', 'RELEASED']),
    winId: z.string().nullable(),
    samplingRuleId: Ulid.nullable(),
    updatedAt: IsoDateTime,
});
export const StockReconciliationDto = z.object({
    lotteryId: Ulid,
    generatedAt: IsoDateTime,
    prizes: z.array(z.object({ prizeId: Ulid, name: z.string(), stock: PrizeStockDto, balanced: z.boolean() })),
    balanced: z.boolean(),
});
// ---------------------------------------------------------------------------------------------------------
// Catalogue builder extensions (prizes module agent, D-1202): assets, products, feeds, stock, substitution.
// ---------------------------------------------------------------------------------------------------------
/** §6.1 category spec templates: the specification keys the admin builder pre-fills per category. */
export const SPEC_TEMPLATES = {
    PHONE: ['brand', 'model', 'storage', 'ram', 'colour'],
    CAR: ['make', 'model', 'trim', 'exteriorColour', 'interiorColour'],
    LAPTOP: ['brand', 'model', 'cpu', 'ram', 'storage'],
    APPLIANCE: ['brand', 'model', 'capacity', 'energyRating', 'colour'],
    FASHION: ['brand', 'size', 'colour', 'material'],
    VOUCHER: ['value', 'issuer', 'redemption', 'expiry'],
    CASH: ['rail', 'currency'],
    OTHER: [],
};
/** Suggested option axes per category (the builder seeds these; organizers edit them). */
export const SPEC_TEMPLATE_AXES = {
    PHONE: ['colour', 'storage'],
    CAR: ['trim', 'exterior_colour'],
    LAPTOP: ['cpu', 'ram', 'storage'],
    APPLIANCE: ['colour'],
    FASHION: ['size', 'colour'],
    VOUCHER: ['value'],
    CASH: [],
    OTHER: [],
};
/** What an uploaded catalogue asset is for; drives the allowed types and minimum dimensions. */
export const AssetPurpose = z.enum(['PRIZE_IMAGE', 'SPONSOR_LOGO', 'BRAND_LOGO', 'PRODUCT_VIDEO']);
export const ASSET_RULES = {
    PRIZE_IMAGE: {
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/avif'],
        maxBytes: 8 * 1024 * 1024,
        minWidth: 320,
        minHeight: 320,
    },
    SPONSOR_LOGO: {
        allowedTypes: ['image/png', 'image/webp', 'image/svg+xml'],
        maxBytes: 1024 * 1024,
        minWidth: 64,
        minHeight: 24,
    },
    BRAND_LOGO: {
        allowedTypes: ['image/png', 'image/webp', 'image/svg+xml'],
        maxBytes: 1024 * 1024,
        minWidth: 64,
        minHeight: 24,
    },
    PRODUCT_VIDEO: { allowedTypes: ['video/mp4'], maxBytes: 64 * 1024 * 1024, minWidth: 0, minHeight: 0 },
};
export const PresignAssetRequest = z.object({
    purpose: AssetPurpose,
    fileName: z.string().min(1).max(160),
    contentType: z.string().min(3).max(80),
    sizeBytes: z.number().int().min(1),
});
export const PresignAssetResponse = z.object({
    assetId: Ulid,
    /** Stable reference to use in `heroImageRef`, `imageRefs`, `logoLightRef` … (`asset:<assetId>`). */
    ref: z.string(),
    upload: z.object({
        url: z.string(),
        method: z.literal('PUT'),
        headers: z.record(z.string(), z.string()),
        expiresAt: IsoDateTime,
        maxBytes: z.number().int(),
    }),
});
/** §6.3 asset validation: alt text and image rights are mandatory before an asset becomes usable. */
export const FinalizeAssetRequest = z.object({
    alt: z.string().min(1).max(240),
    rightsConfirmed: z.literal(true),
});
export const ASSET_REF_PREFIX = 'asset:';
export const isAssetRef = (ref) => ref.startsWith(ASSET_REF_PREFIX);
export const assetIdFromRef = (ref) => isAssetRef(ref) ? ref.slice(ASSET_REF_PREFIX.length) : null;
export const ProductAdminDto = UpsertProductRequest.required({ productId: true }).extend({
    brandName: z.string().nullable(),
    specTemplate: z.array(z.string()),
    variantCount: z.number().int(),
    isActive: z.boolean(),
    assetIssues: z.array(z.object({ ref: z.string(), issue: z.string() })),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ProductListQuery = z.object({
    brandId: Ulid.optional(),
    category: PrizeCategory.optional(),
    q: z.string().max(80).optional(),
});
export const VariantMatrixDto = z.object({ variants: z.array(VariantInput), combinations: z.number().int() });
/** Maximum SKU count the matrix generator produces (keeps the builder responsive; §6.3). */
export const VARIANT_MATRIX_MAX = 256;
/**
 * Cartesian product of the option axes → one SKU per combination (§6.3 "variant matrix generator"). Pure and
 * deterministic: axes in the given order, values in the given order, SKU = `<prefix>-<VALUE1>-<VALUE2>`.
 */
export function generateVariantMatrix(input) {
    const axes = input.optionAxes;
    const total = axes.reduce((n, a) => n * a.values.length, 1);
    if (total > VARIANT_MATRIX_MAX)
        throw new RangeError(`variant matrix of ${total} exceeds ${VARIANT_MATRIX_MAX}`);
    let combos = [{}];
    for (const axis of axes) {
        combos = combos.flatMap((c) => axis.values.map((v) => ({ ...c, [axis.axisId]: v.valueId })));
    }
    const prefix = input.skuPrefix
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    const variants = combos.map((options) => ({
        sku: [prefix, ...axes.map((a) => options[a.axisId].toUpperCase().replace(/[^A-Z0-9]+/g, ''))]
            .filter(Boolean)
            .join('-'),
        options,
        value: input.baseValue,
        specifications: {},
        imageRefs: [],
        stock: 0,
    }));
    return { variants, combinations: total };
}
export const ProductFeedItemDto = z.object({
    feedItemId: Ulid,
    brandId: Ulid,
    brandName: z.string(),
    externalSku: z.string(),
    name: z.string(),
    model: z.string().nullable(),
    options: z.record(z.string(), z.string()),
    specifications: z.record(z.string(), z.string()),
    imageRefs: z.array(z.string()),
    retailValue: MoneyDto,
    stock: z.number().int(),
    approved: z.boolean(),
    approvedBy: Ulid.nullable(),
    approvedAt: IsoDateTime.nullable(),
    isActive: z.boolean(),
    updatedAt: IsoDateTime,
});
export const UpsertProductFeedItemRequest = z.object({
    feedItemId: Ulid.optional(),
    brandId: Ulid,
    externalSku: z.string().min(1).max(64),
    name: z.string().min(1).max(160),
    model: z.string().max(120).optional(),
    options: z.record(z.string(), z.string()).default({}),
    specifications: z.record(z.string(), z.string()).default({}),
    imageRefs: z.array(z.string().max(512)).default([]),
    retailValue: SupportedMoneyDto,
    stock: z.number().int().min(0),
});
export const ApproveFeedItemRequest = z.object({
    approved: z.boolean(),
    note: z.string().max(500).optional(),
});
export const ProductFeedListQuery = z.object({
    brandId: Ulid.optional(),
    approved: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
});
export const BrandListQuery = z.object({
    lotteryId: Ulid.optional(),
    approved: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
});
export const PrizeListQuery = z.object({
    lotteryId: Ulid.optional(),
    tier: PrizeTier.optional(),
    sponsorId: Ulid.optional(),
    q: z.string().max(80).optional(),
});
/** Per-variant stock line of a prize (units are the truth; `stockTotal` is the cached plan). */
export const PrizeVariantAdminDto = z.object({
    variantId: Ulid,
    sku: z.string(),
    options: z.record(z.string(), z.string()),
    value: MoneyDto,
    specifications: z.record(z.string(), z.string()),
    imageRefs: z.array(z.string()),
    stock: PrizeStockDto,
    isActive: z.boolean(),
});
export const PrizeDetailDto = PrizeAdminDto.extend({
    description: LocalizedText,
    heroImageRef: z.string().nullable(),
    galleryRefs: z.array(z.string()),
    eligibleRegions: z.array(z.string()),
    substitutionPolicy: SubstitutionPolicy,
    variants: z.array(PrizeVariantAdminDto),
});
/**
 * `POST /admin/prizes/:id/stock`: add units (per variant), void units or reserve units for the main draw.
 * Reductions below active reservations are refused (§6.2 "cannot be edited downward").
 */
export const StockAdjustRequest = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('ADD'),
        variantId: Ulid.optional(),
        units: z.number().int().min(1).max(10_000),
        unitRefPrefix: z.string().max(32).optional(),
    }),
    z.object({
        action: z.literal('VOID'),
        unitIds: z.array(Ulid).min(1).max(1000),
        reason: z.string().min(3).max(240),
    }),
    z.object({
        action: z.literal('RESERVE_MAIN'),
        variantId: Ulid.optional(),
        units: z.number().int().min(1).max(10_000),
    }),
    z.object({
        action: z.literal('RELEASE_MAIN'),
        variantId: Ulid.optional(),
        units: z.number().int().min(1).max(10_000),
    }),
]);
export const PrizeUnitListQuery = z.object({
    status: PrizeUnitDto.shape.status.optional(),
    variantId: Ulid.optional(),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
/** §6.2 substitution: participant consent reference + organizer approval are recorded, never implied. */
export const SubstitutePrizeUnitRequest = z.object({
    winId: z.string().min(6).max(20),
    toVariantId: Ulid,
    consentRef: z.string().min(3).max(120),
    consentAt: IsoDateTime,
    reason: z.string().min(5).max(500),
});
export const SubstitutionDto = z.object({
    substitutionId: Ulid,
    winId: z.string(),
    prizeId: Ulid,
    fromVariantId: Ulid.nullable(),
    toVariantId: Ulid,
    fromUnitId: Ulid.nullable(),
    toUnitId: Ulid,
    consentRef: z.string(),
    consentAt: IsoDateTime,
    reason: z.string(),
    approvedBy: Ulid,
    approvedAt: IsoDateTime,
});
export const StockReconciliationQuery = z.object({ lotteryId: Ulid });
//# sourceMappingURL=prizes.js.map